import {
  castArray,
  cloneDeep,
  difference,
  get,
  isEmpty,
  isEqual,
  isNil,
  isString,
  keyBy,
  keys,
  last,
  omit,
  sortBy,
  uniq,
} from "lodash";
import { v4 as uuidv4 } from "uuid";
import { getModelName } from "../process-model-names";
import type { RelinkableTask } from "../process-task-relink";
import { planInsertionRewrites } from "../process-task-relink";
import type { nil } from "../type-utils";
import type {
  ProcessModelCopyContainerVisibilityFinding,
  ProcessModelCopyDecisions,
  ProcessModelCopyElement,
  ProcessModelCopyExamPairingFinding,
  ProcessModelCopyExistingNodeType,
  ProcessModelCopyIdConflict,
  ProcessModelCopyIdDecision,
  ProcessModelCopyIdResolution,
  ProcessModelCopyModel,
  ProcessModelCopyReferenceConflict,
  ProcessModelCopyReferenceDecision,
  ProcessModelCopyReferenceResolution,
  ProcessModelCopyResourceFinding,
  ProcessModelCopyRewireCandidate,
  ProcessModelCopyRole,
  ProcessModelCopyRoleConflict,
  ProcessModelCopyRoleDecision,
  ProcessModelCopyRoleResolution,
  ProcessModelCopyTask,
  ProcessModelCopyTranslationFallback,
  ProcessModelCopyUsage,
} from "./process-model-copy.model";
import {
  ProcessModelCopyBlockingReason,
  ProcessModelCopyIdAction,
  ProcessModelCopyLevel,
  ProcessModelCopyMatchReason,
  ProcessModelCopyModelNodeType,
  ProcessModelCopyNodeType,
  ProcessModelCopyPlanError,
  ProcessModelCopyReferenceAction,
  ProcessModelCopyReferenceConflictKind,
  ProcessModelCopyReferenceType,
  ProcessModelCopyRoleConflictKind,
} from "./process-model-copy.model";
import { hasExamPartner, isExamTaskTypeName } from "./process-model-copy-exams";
import type { LabelledCandidate, LabelMatch } from "./process-model-copy-match";
import { findLabelMatch, getMatchLanguages, isValidModelId, suggestFreeModelId } from "./process-model-copy-match";
import type { ReferenceOccurrence, ReferenceToResolve } from "./process-model-copy-references";
import { findReferences, rewriteReferences } from "./process-model-copy-references";
import type { RoleKeyMap, RoleUsage } from "./process-model-copy-roles";
import { applyRoleKeyMap, collectRoleLiteralUsages, collectRoleUsages } from "./process-model-copy-roles";
import type { ProcessModelCopySelection } from "./process-model-copy-selection";
import { groupSelectionByLevel } from "./process-model-copy-selection";
import type { TranslationLanguages } from "./process-model-copy-translations";
import { restrictToTargetLanguages } from "./process-model-copy-translations";

export interface ProcessModelCopyPlanInput {
  level: ProcessModelCopyLevel;
  /** The model, area or phase whose "+" was clicked; the copy is appended at its end. */
  targetContainerModelId: string;
  taskModelIds: readonly string[];
  decisions: ProcessModelCopyDecisions;
  /** The full content of the model to copy from. */
  source: ProcessModelCopyModel;
  /**
   * The open model; its tree with names, task types, predecessors and roles is enough to plan. The hints read more where
   * it has it: the `visibleForRoles` of the container (and of the area holding a phase), and the
   * `languageEducationFields` of exam tasks, without which they pair with no copied one.
   */
  target: ProcessModelCopyModel;
  /** The language display names are shown in. */
  language: string;
  /** Organization-scoped ids of the source cannot be valid in the target then. */
  isCrossOrganization: boolean;
  /** Task model types (e.g. `ProcessTaskSigningModel`) the target organization may not use. */
  deniedTaskTypeNames: readonly string[];
  /** Whether a modelId of the user's choice is accepted (Ankaadia users only). */
  canChooseModelIds: boolean;
  generateId?: () => string;
}

/** A copied area, phase or task, with the modelId it gets in the target. */
export interface ProcessModelCopyCopiedElement<T extends ProcessModelCopyElement = ProcessModelCopyElement> {
  nodeType: ProcessModelCopyNodeType;
  sourceModelId: string;
  modelId: string;
  /** The element as it is created: cleaned, with its final modelId, references and roles. Areas and phases come without children. */
  node: T;
}

export interface ProcessModelCopyPlannedTask {
  sourceModelId: string;
  task: ProcessModelCopyTask;
}

/** A phase of the copy: a new one, or (with `existingTargetModelId`) an existing target phase that takes the tasks. */
export interface ProcessModelCopyPlannedPhase {
  sourceModelId: string;
  modelId: string;
  phase: ProcessModelCopyElement | null;
  existingTargetModelId: string | null;
  tasks: ProcessModelCopyPlannedTask[];
}

/** An area of the copy: a new one, or (with `existingTargetModelId`) an existing target area that takes the phases. */
export interface ProcessModelCopyPlannedArea {
  sourceModelId: string;
  modelId: string;
  area: ProcessModelCopyElement | null;
  existingTargetModelId: string | null;
  phases: ProcessModelCopyPlannedPhase[];
}

/** A target task that waits for the copied tasks, besides the task before the insertion point unless they do. */
export interface ProcessModelCopyRewire {
  taskModelId: string;
  previousPredecessorsIds: string[];
  predecessorsIds: string[];
}

export interface ProcessModelCopyContainer {
  modelId: string;
  /** The container's last child, which the copy is appended after. */
  lastChildModelId: string | null;
}

export interface ProcessModelCopyPlan {
  level: ProcessModelCopyLevel;
  container: ProcessModelCopyContainer;
  /** The task directly before the insertion point: target tasks waiting only for it may wait for the copy instead. */
  insertionPointTaskModelId: string | null;
  /** At task level: the copied tasks. */
  tasks: ProcessModelCopyPlannedTask[];
  /** At phase level: the phases of the copy. */
  phases: ProcessModelCopyPlannedPhase[];
  /** At area level: the areas of the copy. */
  areas: ProcessModelCopyPlannedArea[];
  /** Every element that is created, in tree order (areas, then phases, then tasks). */
  copiedElements: ProcessModelCopyCopiedElement[];
  /** The rewires that are applied: ticked and not blocked. */
  rewires: ProcessModelCopyRewire[];
  roleConflicts: ProcessModelCopyRoleConflict[];
  idConflicts: ProcessModelCopyIdConflict[];
  referenceConflicts: ProcessModelCopyReferenceConflict[];
  rewireCandidates: ProcessModelCopyRewireCandidate[];
  translationFallbacks: ProcessModelCopyTranslationFallback[];
  resourceFindings: ProcessModelCopyResourceFinding[];
  examPairingFindings: ProcessModelCopyExamPairingFinding[];
  containerVisibilityFindings: ProcessModelCopyContainerVisibilityFinding[];
  /** Selected tasks whose type the target organization may not use. */
  deniedTaskModelIds: string[];
  blockingReasons: ProcessModelCopyBlockingReason[];
  conflictCount: number;
  reviewRequiredCount: number;
  /** Whether nothing stops the copy: no unresolved or blocking conflict, nothing denied, something to copy. */
  isApplicable: boolean;
}

export type ProcessModelCopyPlanResult =
  | { plan: ProcessModelCopyPlan; error?: never }
  | { plan?: never; error: ProcessModelCopyPlanError };

interface IndexedElement {
  nodeType: ProcessModelCopyNodeType;
  modelId: string;
  node: ProcessModelCopyElement;
}

interface IndexedTask {
  task: ProcessModelCopyTask;
  areaIndex: number;
  phaseIndex: number;
}

interface ModelIndex {
  elementsByModelId: ReadonlyMap<string, IndexedElement>;
  /** The tasks in tree order, with the position of the area and phase they are in. */
  tasks: IndexedTask[];
}

/** What has a selected element's modelId in the target already. */
interface ExistingElement {
  nodeType: ProcessModelCopyExistingNodeType;
  node: Pick<ProcessModelCopyElement, "modelId" | "displayName">;
}

interface SelectedElement {
  nodeType: ProcessModelCopyNodeType;
  sourceModelId: string;
  node: ProcessModelCopyElement;
}

interface IdPlan {
  conflicts: ProcessModelCopyIdConflict[];
  modelIdBySourceModelId: ReadonlyMap<string, string>;
  /** Selected elements whose existing target namesake is used instead of a copy. */
  existingModelIds: ReadonlySet<string>;
}

interface RolePlan {
  conflicts: ProcessModelCopyRoleConflict[];
  roleKeyMap: RoleKeyMap;
}

interface ReferencePlan {
  conflicts: ProcessModelCopyReferenceConflict[];
  tasks: ProcessModelCopyCopiedElement<ProcessModelCopyTask>[];
}

interface PlanContext {
  input: ProcessModelCopyPlanInput;
  sourceIndex: ModelIndex;
  targetIndex: ModelIndex;
  matchLanguages: string[];
}

interface Resolved<R> {
  resolution: R | null;
  hasInvalidDecision: boolean;
}

interface ContainerEnd {
  areaIndex: number;
  phaseIndex: number;
}

interface EnclosingContainer {
  nodeType: ProcessModelCopyContainerVisibilityFinding["nodeType"];
  node: ProcessModelCopyElement;
}

type PlanStructure = Pick<ProcessModelCopyPlan, "tasks" | "phases" | "areas">;

// What a copy never takes over from the source: storage fields, directive leftovers, the nested children (planned on
// their own) and fields no command input has. Storage metadata starting with "_" goes anyway, see
// `isStorageMetadataKey`, so only the fields without one are listed
const NOT_COPIED_FIELDS = [
  "id",
  "organizationId",
  "parentId",
  "rootId",
  "sortOrder",
  "ttl",
  "directive",
  "referenceId",
  "phaseId",
  "areaId",
  "previousTaskId",
  "previousPhaseId",
  "previousAreaId",
  "formly",
  "phases",
  "tasks",
];

// The only fields starting with "_" that a create input has: the discriminators
const COPIED_UNDERSCORE_FIELDS = ["_typeName", "_className"];

// Fields of a rule that the rule input of a command does not have (deprecated, kept in stored models)
const NOT_COPIED_RULE_FIELDS = ["taskId", "priority"];

// Ids of an organization's own documents and exams, meaningless in another organization
const ORGANIZATION_SCOPED_ID_FIELDS = ["candidateRequiredDocumentSetIds", "examId", "examOrganizationId"] as const;

// The role fields naming whom a task is for, whom the area and phase holding it should be visible for as well
const TASK_AUDIENCE_ROLE_FIELDS: readonly string[] = ["visibleForRoles", "targetRoles", "responsibleRoles"];

/**
 * Plans copying the selected source tasks (as tasks, or grouped into new phases or areas, depending on the level) to
 * the end of a target container: which elements are created with which modelIds, every conflict with its suggestion
 * and the decision applied to it, the rewritten references and roles and the target tasks that wait for the copy.
 * Pure: it reads the models it is given and changes nothing. See `buildCopyCommands` for the commands that apply it.
 */
export function createCopyPlan(input: ProcessModelCopyPlanInput): ProcessModelCopyPlanResult {
  const { level, source, target } = input;
  const container = getContainer(target, level, input.targetContainerModelId);
  if (isNil(container)) {
    return { error: ProcessModelCopyPlanError.InvalidTarget };
  }

  const selection = groupSelectionByLevel(source.areas ?? [], input.taskModelIds, level);
  if (!isEmpty(selection.unknownTaskModelIds) || isEmpty(selection.taskModelIds)) {
    return { error: ProcessModelCopyPlanError.InvalidSelection };
  }

  const context: PlanContext = {
    input,
    sourceIndex: indexModel(source),
    targetIndex: indexModel(target),
    matchLanguages: getMatchLanguages(target.rootLanguage, target.languages ?? [], source.languages ?? []),
  };
  const selectedElements = getSelectedElements(selection, context.sourceIndex);
  const idPlan = planIds(selectedElements, context);
  const cleanedElements = selectedElements
    .filter(({ sourceModelId }) => !idPlan.existingModelIds.has(sourceModelId))
    .map((element) => toCopiedElement(element, idPlan, input.generateId ?? uuidv4));

  const { conflicts: roleConflicts, roleKeyMap } = planRoles(cleanedElements, context);
  const withRoles = cleanedElements.map((element) => ({
    ...element,
    node: applyRoleKeyMap(element.node, roleKeyMap),
  }));

  const insertionPointTaskModelId = findInsertionPointTask(context.targetIndex, target, level, container.modelId);
  // A copied task left without predecessors starts right away, as it would in the source
  const referencePlan = planReferences(withRoles.filter(isTaskElement), idPlan, context);

  const translationLanguages: TranslationLanguages = {
    targetLanguages: target.languages ?? [],
    targetRootLanguage: target.rootLanguage,
    sourceRootLanguage: source.rootLanguage,
  };
  const finalTasksBySourceModelId = keyBy(referencePlan.tasks, ({ sourceModelId }) => sourceModelId);
  const withFinalTasks = withRoles.map((element) =>
    isTaskElement(element) ? (finalTasksBySourceModelId[element.sourceModelId] ?? element) : element
  );
  const translated = withFinalTasks.map((element) => {
    const { node, fallbacks } = restrictToTargetLanguages(element.node, translationLanguages);
    return { element: { ...element, node }, fallbacks };
  });
  const resources = translated.map(({ element }) => clearOrganizationScopedIds(element, input.isCrossOrganization));
  const copiedElements = resources.map(({ element }) => element);

  const copiedTasks = copiedElements.filter(isTaskElement);
  const rewireCandidates = planRewires(copiedTasks, insertionPointTaskModelId, context);
  const deniedTaskModelIds = copiedTasks
    .filter(({ node }) => input.deniedTaskTypeNames.includes(node._typeName))
    .map(({ sourceModelId }) => sourceModelId);
  const blockingReasons = getBlockingReasons({
    roleConflicts,
    referenceConflicts: referencePlan.conflicts,
    deniedTaskModelIds,
    copiedElements,
  });
  const conflicts = [...roleConflicts, ...idPlan.conflicts, ...referencePlan.conflicts];

  return {
    plan: {
      level,
      container,
      insertionPointTaskModelId,
      ...assembleStructure(selection, copiedElements, idPlan, level),
      copiedElements,
      rewires: rewireCandidates
        .filter(({ isApplied }) => isApplied)
        .map(({ taskModelId, previousPredecessorsIds, predecessorsIds }) => ({
          taskModelId,
          previousPredecessorsIds,
          predecessorsIds,
        })),
      roleConflicts,
      idConflicts: idPlan.conflicts,
      referenceConflicts: referencePlan.conflicts,
      rewireCandidates,
      translationFallbacks: translated.flatMap(({ element, fallbacks }) =>
        fallbacks.map((fallback) => ({ sourceModelId: element.sourceModelId, modelId: element.modelId, ...fallback }))
      ),
      resourceFindings: resources.flatMap(({ findings }) => findings),
      examPairingFindings: findUnpairedExamTasks(copiedTasks, context.targetIndex),
      containerVisibilityFindings: findHiddenContainers(copiedTasks, input),
      deniedTaskModelIds,
      blockingReasons,
      conflictCount: conflicts.length + rewireCandidates.length,
      reviewRequiredCount: conflicts.filter(({ isReviewRequired }) => isReviewRequired).length,
      isApplicable: isEmpty(blockingReasons),
    },
  };
}

function getContainer(
  target: ProcessModelCopyModel,
  level: ProcessModelCopyLevel,
  containerModelId: string
): ProcessModelCopyContainer | null {
  const areas = target.areas ?? [];
  switch (level) {
    case ProcessModelCopyLevel.Area:
      return containerModelId === target.modelId ? toContainer(containerModelId, areas) : null;
    case ProcessModelCopyLevel.Phase: {
      const area = areas.find(({ modelId }) => modelId === containerModelId);
      return isNil(area) ? null : toContainer(containerModelId, area.phases ?? []);
    }

    case ProcessModelCopyLevel.Task: {
      const phase = areas.flatMap((area) => area.phases ?? []).find(({ modelId }) => modelId === containerModelId);
      return isNil(phase) ? null : toContainer(containerModelId, phase.tasks ?? []);
    }

    default: {
      const unhandled: never = level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

function toContainer(modelId: string, children: readonly ProcessModelCopyElement[]): ProcessModelCopyContainer {
  return { modelId, lastChildModelId: last(children)?.modelId ?? null };
}

function indexModel(model: ProcessModelCopyModel): ModelIndex {
  const areas = model.areas ?? [];
  const phases = areas.flatMap((area) => area.phases ?? []);
  const tasks = areas.flatMap((area, areaIndex) =>
    (area.phases ?? []).flatMap((phase, phaseIndex) =>
      (phase.tasks ?? []).map((task) => ({ task, areaIndex, phaseIndex }))
    )
  );
  const elements: IndexedElement[] = [
    ...areas.map((node) => ({ nodeType: ProcessModelCopyNodeType.Area, modelId: node.modelId, node })),
    ...phases.map((node) => ({ nodeType: ProcessModelCopyNodeType.Phase, modelId: node.modelId, node })),
    ...tasks.map(({ task }) => ({ nodeType: ProcessModelCopyNodeType.Task, modelId: task.modelId, node: task })),
  ];
  // The first of duplicate modelIds wins, as validation reports the duplicates anyway; the map keeps tree order
  const elementsByModelId = elements.reduce(
    (byModelId, element) => (byModelId.has(element.modelId) ? byModelId : byModelId.set(element.modelId, element)),
    new Map<string, IndexedElement>()
  );
  return { elementsByModelId, tasks };
}

function getSelectedElements(selection: ProcessModelCopySelection, sourceIndex: ModelIndex): SelectedElement[] {
  const toElement = (modelId: string): SelectedElement => {
    const { nodeType, node } = sourceIndex.elementsByModelId.get(modelId);
    return { nodeType, sourceModelId: modelId, node };
  };

  switch (selection.level) {
    case ProcessModelCopyLevel.Task:
      return selection.taskModelIds.map(toElement);
    case ProcessModelCopyLevel.Phase:
      return [...selection.phases.map(({ modelId }) => toElement(modelId)), ...selection.taskModelIds.map(toElement)];
    case ProcessModelCopyLevel.Area:
      return [
        ...selection.areas.map(({ modelId }) => toElement(modelId)),
        ...selection.areas.flatMap(({ phases }) => phases.map(({ modelId }) => toElement(modelId))),
        ...selection.taskModelIds.map(toElement),
      ];
    default: {
      const unhandled: never = selection.level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

/**
 * Conflict #3: a selected element whose modelId the target has already, for one of its elements or the model itself.
 * Same type and same name suggest using the existing element, anything else a new modelId (`<modelId>-N`). The
 * modelIds every copy ends up with are unique among themselves and in the target.
 */
function planIds(selectedElements: SelectedElement[], context: PlanContext): IdPlan {
  const { input, targetIndex, matchLanguages } = context;
  const takenModelIds = new Set([
    input.target.modelId,
    ...targetIndex.elementsByModelId.keys(),
    ...selectedElements.map(({ sourceModelId }) => sourceModelId),
  ]);
  const decisionsBySourceModelId = keyBy(input.decisions.idDecisions ?? [], ({ sourceModelId }) => sourceModelId);
  const modelIdBySourceModelId = new Map<string, string>();
  const existingModelIds = new Set<string>();
  const conflicts: ProcessModelCopyIdConflict[] = [];

  selectedElements.forEach(({ sourceModelId, nodeType, node }) => {
    const existing = findExistingElement(sourceModelId, context);
    if (isNil(existing)) {
      modelIdBySourceModelId.set(sourceModelId, sourceModelId);
      return;
    }

    const isSameType = existing.nodeType === nodeType;
    const isSameLabel =
      isSameType &&
      findLabelMatch(
        node.displayName,
        [{ key: existing.node.modelId, displayName: existing.node.displayName }],
        matchLanguages
      ).reason === ProcessModelCopyMatchReason.SameLabel;
    const suggestedModelId = suggestFreeModelId(sourceModelId, takenModelIds);
    const suggestion: ProcessModelCopyIdResolution = isSameLabel
      ? { action: ProcessModelCopyIdAction.UseExisting, reason: ProcessModelCopyMatchReason.SameLabel }
      : {
          action: ProcessModelCopyIdAction.Rename,
          newModelId: suggestedModelId,
          reason: ProcessModelCopyMatchReason.SameId,
        };
    const { resolution, hasInvalidDecision } = resolveIdDecision(decisionsBySourceModelId[sourceModelId], {
      isSameType,
      suggestedModelId,
      takenModelIds,
      canChooseModelIds: input.canChooseModelIds,
    });
    const applied = resolution ?? suggestion;
    if (applied.action === ProcessModelCopyIdAction.UseExisting) {
      existingModelIds.add(sourceModelId);
    } else {
      takenModelIds.add(applied.newModelId);
      modelIdBySourceModelId.set(sourceModelId, applied.newModelId);
    }

    conflicts.push({
      key: `id:${sourceModelId}`,
      sourceModelId,
      nodeType,
      displayName: getModelName(node, input.language),
      existingNodeType: existing.nodeType,
      existingDisplayName: getModelName(existing.node, input.language),
      suggestion,
      resolution: applied,
      canChooseModelId: input.canChooseModelIds,
      isReviewRequired: false,
      isResolved: true,
      hasInvalidDecision,
    });
  });

  return { conflicts, modelIdBySourceModelId, existingModelIds };
}

/**
 * The target's element with the modelId: an area, phase or task, or the model itself, whose modelId validation wants
 * unique among its elements as well.
 */
function findExistingElement(modelId: string, { input, targetIndex }: PlanContext): ExistingElement | null {
  const { target } = input;
  if (modelId === target.modelId) {
    return { nodeType: ProcessModelCopyModelNodeType.Model, node: { modelId, displayName: target.displayName } };
  }

  return targetIndex.elementsByModelId.get(modelId) ?? null;
}

interface IdDecisionContext {
  isSameType: boolean;
  suggestedModelId: string;
  takenModelIds: ReadonlySet<string>;
  canChooseModelIds: boolean;
}

function resolveIdDecision(
  decision: ProcessModelCopyIdDecision | nil,
  { isSameType, suggestedModelId, takenModelIds, canChooseModelIds }: IdDecisionContext
): Resolved<ProcessModelCopyIdResolution> {
  if (isNil(decision)) {
    return { resolution: null, hasInvalidDecision: false };
  }

  if (decision.action === ProcessModelCopyIdAction.UseExisting) {
    return isSameType
      ? { resolution: { action: ProcessModelCopyIdAction.UseExisting }, hasInvalidDecision: false }
      : { resolution: null, hasInvalidDecision: true };
  }

  const suggestedRename = { action: ProcessModelCopyIdAction.Rename, newModelId: suggestedModelId };
  if (isNil(decision.newModelId) || decision.newModelId === suggestedModelId) {
    return { resolution: suggestedRename, hasInvalidDecision: false };
  }

  const isUsable = canChooseModelIds && isValidModelId(decision.newModelId) && !takenModelIds.has(decision.newModelId);
  return isUsable
    ? {
        resolution: { action: ProcessModelCopyIdAction.Rename, newModelId: decision.newModelId },
        hasInvalidDecision: false,
      }
    : { resolution: suggestedRename, hasInvalidDecision: true };
}

function toCopiedElement(
  element: SelectedElement,
  idPlan: IdPlan,
  generateId: () => string
): ProcessModelCopyCopiedElement {
  const modelId = idPlan.modelIdBySourceModelId.get(element.sourceModelId);
  const storageMetadataKeys = keys(element.node).filter(isStorageMetadataKey);
  const cleaned = omit(cloneDeep(element.node), [...NOT_COPIED_FIELDS, ...storageMetadataKeys]);
  const rules: Record<string, unknown>[] | nil = get(cleaned, "rules");
  // A rule's id is unique across the model, and the source's rules keep theirs
  const withRules = isNil(rules)
    ? cleaned
    : { ...cleaned, rules: rules.map((rule) => ({ ...omit(rule, NOT_COPIED_RULE_FIELDS), id: generateId() })) };
  return {
    nodeType: element.nodeType,
    sourceModelId: element.sourceModelId,
    modelId,
    node: { ...withRules, modelId },
  };
}

/**
 * Whether a stored field is metadata of the database (`_etag`, `_ts`, `_rid`, the `_ots` of a restored backup, …),
 * which no command input has: GraphQL refuses the whole batch for one unknown field.
 */
function isStorageMetadataKey(key: string): boolean {
  return key.startsWith("_") && !COPIED_UNDERSCORE_FIELDS.includes(key);
}

/**
 * Conflicts #1, #2 and #8: the role keys the copied elements use. A key the target lacks is mapped to a target role:
 * the one with the same name, or the first of several in the target's order (for the user to check). With none, the
 * user picks one, and the copy waits for it. A key the target defines differently stays (same key, same role) and is
 * only reported. Role literals in expressions follow the mapping.
 */
function planRoles(elements: ProcessModelCopyCopiedElement[], { input, matchLanguages }: PlanContext): RolePlan {
  const { source, target, language } = input;
  const usages = elements.flatMap(({ node, sourceModelId }) => collectRoleUsages(node, sourceModelId));
  const literalUsages = elements
    .filter(isTaskElement)
    .flatMap(({ node, sourceModelId }) => collectRoleLiteralUsages(node, sourceModelId));
  const roleKeys = uniq([...usages, ...literalUsages].map(({ roleKey }) => roleKey));
  const sourceRolesByKey = keyBy(source.roles ?? [], ({ processRole }) => processRole?.trim());
  const targetRoles = target.roles ?? [];
  const targetRolesByKey = keyBy(targetRoles, ({ processRole }) => processRole?.trim());
  const targetCandidates: LabelledCandidate[] = targetRoles.map(({ processRole, displayName }) => ({
    key: processRole.trim(),
    displayName,
  }));
  const decisionsByRoleKey = keyBy(input.decisions.roleDecisions ?? [], ({ roleKey }) => roleKey.trim());

  const conflicts = roleKeys.flatMap((roleKey): ProcessModelCopyRoleConflict[] => {
    const sourceRole = sourceRolesByKey[roleKey];
    const targetRole = targetRolesByKey[roleKey];
    const hasDefinitionDiff =
      !isNil(targetRole) && !isNil(sourceRole) && !isSameRoleDefinition(sourceRole, targetRole, matchLanguages);
    if (!isNil(targetRole) && !hasDefinitionDiff) {
      return [];
    }

    const match = isNil(targetRole) ? findLabelMatch(sourceRole?.displayName, targetCandidates, matchLanguages) : null;
    const suggestion = isNil(match)
      ? mapToRole(roleKey, ProcessModelCopyMatchReason.SameId)
      : suggestMissingRole(match);
    const { resolution, hasInvalidDecision } = resolveRoleDecision(decisionsByRoleKey[roleKey], targetRolesByKey);
    const applied = resolution ?? suggestion;
    return [
      {
        key: `role:${roleKey}`,
        kind: isNil(targetRole)
          ? ProcessModelCopyRoleConflictKind.MissingRole
          : ProcessModelCopyRoleConflictKind.RoleDefinitionDiffers,
        roleKey,
        displayName: getRoleName(sourceRole, language),
        usages: toUsages(usages, roleKey),
        literalUsages: toUsages(literalUsages, roleKey),
        candidates:
          match?.reason === ProcessModelCopyMatchReason.AmbiguousLabel
            ? match.keys.map((key) => ({ roleKey: key, displayName: getRoleName(targetRolesByKey[key], language) }))
            : [],
        suggestion,
        resolution: applied,
        isReviewRequired: isNil(suggestion) || suggestion.reason === ProcessModelCopyMatchReason.AmbiguousLabel,
        isResolved: !isNil(applied),
        hasInvalidDecision,
      },
    ];
  });

  const roleKeyMap = new Map(
    conflicts
      .filter(({ resolution }) => !isNil(resolution))
      .map(({ roleKey, resolution }): [string, string] => [roleKey, resolution.targetRoleKey])
  );
  return { conflicts, roleKeyMap };
}

function suggestMissingRole(match: LabelMatch): ProcessModelCopyRoleResolution | null {
  switch (match.reason) {
    case ProcessModelCopyMatchReason.SameLabel:
    case ProcessModelCopyMatchReason.AmbiguousLabel:
      return mapToRole(match.keys[0], match.reason);
    case ProcessModelCopyMatchReason.NoMatch:
      return null;
    default: {
      const unhandled: never = match.reason;
      throw new Error(`Unknown match reason ${unhandled}`);
    }
  }
}

function mapToRole(targetRoleKey: string, reason: ProcessModelCopyMatchReason): ProcessModelCopyRoleResolution {
  return { targetRoleKey, reason };
}

function resolveRoleDecision(
  decision: ProcessModelCopyRoleDecision | nil,
  targetRolesByKey: Readonly<Record<string, ProcessModelCopyRole>>
): Resolved<ProcessModelCopyRoleResolution> {
  if (isNil(decision)) {
    return { resolution: null, hasInvalidDecision: false };
  }

  const targetRoleKey = decision.targetRoleKey?.trim();
  return isNil(targetRolesByKey[targetRoleKey])
    ? { resolution: null, hasInvalidDecision: true }
    : { resolution: { targetRoleKey }, hasInvalidDecision: false };
}

function isSameRoleDefinition(
  sourceRole: ProcessModelCopyRole,
  targetRole: ProcessModelCopyRole,
  languages: string[]
): boolean {
  const isSameName =
    findLabelMatch(
      sourceRole.displayName,
      [{ key: targetRole.processRole, displayName: targetRole.displayName }],
      languages
    ).reason === ProcessModelCopyMatchReason.SameLabel;
  const isSameOptionality = Boolean(sourceRole.isOptional) === Boolean(targetRole.isOptional);
  const hasSameVisibleStates = isEqual(sortBy(sourceRole.visibleStates ?? []), sortBy(targetRole.visibleStates ?? []));
  return isSameName && isSameOptionality && hasSameVisibleStates;
}

function getVisibleForRoles(node: ProcessModelCopyElement | nil): string[] {
  return (node?.visibleForRoles ?? []).map((role) => role?.trim());
}

/**
 * Conflicts #4, #5 and #6: references of the copied tasks to elements that are not copied. One the target has by
 * modelId stays linked (template lineage); for any other the target's element with the same name is suggested, or
 * else the reference is removed from a field and kept in an expression. References within the copy follow renamed ids.
 */
function planReferences(
  tasks: ProcessModelCopyCopiedElement<ProcessModelCopyTask>[],
  idPlan: IdPlan,
  context: PlanContext
): ReferencePlan {
  const { input, sourceIndex } = context;
  const knownModelIds = new Set(sourceIndex.elementsByModelId.keys());
  const decisionsByKey = keyBy(
    input.decisions.referenceDecisions ?? [],
    ({ sourceModelId, field, referencedModelId }) => toReferenceKey(sourceModelId, field, referencedModelId)
  );
  const getCopyModelId = (referencedModelId: string): string | null =>
    idPlan.modelIdBySourceModelId.get(referencedModelId) ??
    (idPlan.existingModelIds.has(referencedModelId) ? referencedModelId : null);

  const conflicts = tasks.flatMap(({ node, sourceModelId }) =>
    findReferences(node, knownModelIds).flatMap((occurrence) =>
      occurrence.referencedModelIds
        .filter((referencedModelId) => isNil(getCopyModelId(referencedModelId)))
        .map((referencedModelId) =>
          toReferenceConflict({ sourceModelId, occurrence, referencedModelId, decisionsByKey, context })
        )
    )
  );
  const resolutionsByKey = new Map(conflicts.map(({ key, resolution }) => [key, resolution]));

  const rewrittenTasks = tasks.map((task) => {
    const resolve = ({ field, referencedModelId }: ReferenceToResolve): string | null => {
      const copyModelId = getCopyModelId(referencedModelId);
      if (!isNil(copyModelId)) {
        return copyModelId;
      }

      const resolution = resolutionsByKey.get(toReferenceKey(task.sourceModelId, field, referencedModelId));
      switch (resolution?.action) {
        case ProcessModelCopyReferenceAction.Link:
          return resolution.targetModelId;
        case ProcessModelCopyReferenceAction.Remove:
          return null;
        default:
          return referencedModelId;
      }
    };
    return { ...task, node: rewriteReferences(task.node, knownModelIds, resolve) };
  });

  return { conflicts, tasks: rewrittenTasks };
}

interface ReferenceConflictOptions {
  sourceModelId: string;
  occurrence: ReferenceOccurrence;
  referencedModelId: string;
  decisionsByKey: Readonly<Record<string, ProcessModelCopyReferenceDecision>>;
  context: PlanContext;
}

function toReferenceConflict({
  sourceModelId,
  occurrence,
  referencedModelId,
  decisionsByKey,
  context,
}: ReferenceConflictOptions): ProcessModelCopyReferenceConflict {
  const { input, sourceIndex, targetIndex, matchLanguages } = context;
  const { field, type, referencedNodeType, expression } = occurrence;
  const key = toReferenceKey(sourceModelId, field, referencedModelId);
  const isLinkedToExisting = targetIndex.elementsByModelId.has(referencedModelId);
  const referencedSource = sourceIndex.elementsByModelId.get(referencedModelId);
  const targetCandidates: LabelledCandidate[] = Array.from(targetIndex.elementsByModelId.values())
    .filter(({ nodeType }) => nodeType === referencedNodeType)
    .map(({ modelId, node }) => ({ key: modelId, displayName: node.displayName }));
  const match = isLinkedToExisting
    ? null
    : findLabelMatch(referencedSource?.node.displayName, targetCandidates, matchLanguages);
  const suggestion = isLinkedToExisting
    ? { action: ProcessModelCopyReferenceAction.Keep, reason: ProcessModelCopyMatchReason.SameId }
    : suggestReference(match, type);
  const kind = getReferenceConflictKind(isLinkedToExisting, type);
  const { resolution, hasInvalidDecision } = resolveReferenceDecision(decisionsByKey[key], {
    kind,
    type,
    referencedNodeType,
    targetIndex,
  });
  const applied = resolution ?? suggestion;

  return {
    key,
    kind,
    referenceType: type,
    referencedNodeType,
    sourceModelId,
    field,
    referencedModelId,
    referencedDisplayName: isNil(referencedSource) ? "" : getModelName(referencedSource.node, input.language),
    ...(isNil(expression) ? {} : { expression }),
    candidates:
      match?.reason === ProcessModelCopyMatchReason.AmbiguousLabel
        ? match.keys.map((modelId) => ({
            modelId,
            displayName: getModelName(targetIndex.elementsByModelId.get(modelId).node, input.language),
          }))
        : [],
    suggestion,
    resolution: applied,
    isReviewRequired: isNil(suggestion) || suggestion.reason === ProcessModelCopyMatchReason.NoMatch,
    isResolved: !isNil(applied),
    hasInvalidDecision,
  };
}

function getReferenceConflictKind(
  isLinkedToExisting: boolean,
  type: ProcessModelCopyReferenceType
): ProcessModelCopyReferenceConflictKind {
  if (isLinkedToExisting) {
    return ProcessModelCopyReferenceConflictKind.LinkedToExistingElement;
  }

  return type === ProcessModelCopyReferenceType.Field
    ? ProcessModelCopyReferenceConflictKind.MissingReference
    : ProcessModelCopyReferenceConflictKind.UnresolvedExpressionReference;
}

function suggestReference(
  match: LabelMatch,
  type: ProcessModelCopyReferenceType
): ProcessModelCopyReferenceResolution | null {
  switch (match.reason) {
    case ProcessModelCopyMatchReason.SameLabel:
      return { action: ProcessModelCopyReferenceAction.Link, targetModelId: match.keys[0], reason: match.reason };
    case ProcessModelCopyMatchReason.AmbiguousLabel:
      return null;
    case ProcessModelCopyMatchReason.NoMatch:
      return type === ProcessModelCopyReferenceType.Field
        ? { action: ProcessModelCopyReferenceAction.Remove, reason: match.reason }
        : { action: ProcessModelCopyReferenceAction.Keep, reason: match.reason };
    default: {
      const unhandled: never = match.reason;
      throw new Error(`Unknown match reason ${unhandled}`);
    }
  }
}

interface ReferenceDecisionContext {
  kind: ProcessModelCopyReferenceConflictKind;
  type: ProcessModelCopyReferenceType;
  referencedNodeType: ProcessModelCopyNodeType;
  targetIndex: ModelIndex;
}

function resolveReferenceDecision(
  decision: ProcessModelCopyReferenceDecision | nil,
  { kind, type, referencedNodeType, targetIndex }: ReferenceDecisionContext
): Resolved<ProcessModelCopyReferenceResolution> {
  if (isNil(decision)) {
    return { resolution: null, hasInvalidDecision: false };
  }

  switch (decision.action) {
    case ProcessModelCopyReferenceAction.Link: {
      const linked = targetIndex.elementsByModelId.get(decision.targetModelId);
      return linked?.nodeType === referencedNodeType
        ? {
            resolution: { action: ProcessModelCopyReferenceAction.Link, targetModelId: decision.targetModelId },
            hasInvalidDecision: false,
          }
        : { resolution: null, hasInvalidDecision: true };
    }

    case ProcessModelCopyReferenceAction.Remove: {
      const canRemove = [ProcessModelCopyReferenceType.Field, ProcessModelCopyReferenceType.RuleTarget].includes(type);
      return canRemove
        ? { resolution: { action: ProcessModelCopyReferenceAction.Remove }, hasInvalidDecision: false }
        : { resolution: null, hasInvalidDecision: true };
    }

    // A field naming a task the target does not have fails validation; a decision made while it had one no longer holds
    case ProcessModelCopyReferenceAction.Keep:
      return kind === ProcessModelCopyReferenceConflictKind.MissingReference
        ? { resolution: null, hasInvalidDecision: true }
        : { resolution: { action: ProcessModelCopyReferenceAction.Keep }, hasInvalidDecision: false };
    default: {
      const unhandled: never = decision.action;
      throw new Error(`Unknown reference action ${unhandled}`);
    }
  }
}

function toReferenceKey(sourceModelId: string, field: string, referencedModelId: string): string {
  return `reference:${sourceModelId}:${field}:${referencedModelId}`;
}

/**
 * The task directly before the insertion point: the last task inside the container, or, when it holds none, the last
 * task before it in the tree. The editor gives a new task the same predecessor.
 */
function findInsertionPointTask(
  targetIndex: ModelIndex,
  target: ProcessModelCopyModel,
  level: ProcessModelCopyLevel,
  containerModelId: string
): string | null {
  const end = getContainerEnd(target, level, containerModelId);
  const tasksUpToContainerEnd = targetIndex.tasks.filter(
    ({ areaIndex, phaseIndex }) =>
      areaIndex < end.areaIndex || (areaIndex === end.areaIndex && phaseIndex <= end.phaseIndex)
  );
  return last(tasksUpToContainerEnd)?.task.modelId ?? null;
}

/** Where a container ends in the tree, as the position of its last phase. */
function getContainerEnd(
  target: ProcessModelCopyModel,
  level: ProcessModelCopyLevel,
  containerModelId: string
): ContainerEnd {
  const areas = target.areas ?? [];
  switch (level) {
    case ProcessModelCopyLevel.Area:
      return { areaIndex: areas.length, phaseIndex: 0 };
    case ProcessModelCopyLevel.Phase:
      return {
        areaIndex: areas.findIndex(({ modelId }) => modelId === containerModelId),
        phaseIndex: Number.MAX_SAFE_INTEGER,
      };
    case ProcessModelCopyLevel.Task: {
      const areaIndex = areas.findIndex((area) =>
        (area.phases ?? []).some(({ modelId }) => modelId === containerModelId)
      );
      const phaseIndex = (areas[areaIndex]?.phases ?? []).findIndex(({ modelId }) => modelId === containerModelId);
      return { areaIndex, phaseIndex };
    }

    default: {
      const unhandled: never = level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

function clearOrganizationScopedIds(
  element: ProcessModelCopyCopiedElement,
  isCrossOrganization: boolean
): { element: ProcessModelCopyCopiedElement; findings: ProcessModelCopyResourceFinding[] } {
  if (!isCrossOrganization) {
    return { element, findings: [] };
  }

  const clearedFields = ORGANIZATION_SCOPED_ID_FIELDS.filter((field) => !isEmpty(element.node[field]));
  if (isEmpty(clearedFields)) {
    return { element, findings: [] };
  }

  return {
    element: { ...element, node: { ...omit(element.node, clearedFields), modelId: element.node.modelId } },
    findings: clearedFields.map((field) => ({
      sourceModelId: element.sourceModelId,
      field,
      values: toStringList(element.node[field]),
    })),
  };
}

/** Conflict #7: target tasks waiting solely for the task before the insertion point wait for the copy as well. */
function planRewires(
  copiedTasks: ProcessModelCopyCopiedElement<ProcessModelCopyTask>[],
  insertionPointTaskModelId: string | null,
  { input, targetIndex }: PlanContext
): ProcessModelCopyRewireCandidate[] {
  const skipped = input.decisions.skippedRewireTaskModelIds ?? [];
  const toRelinkable = (task: ProcessModelCopyTask): RelinkableTask => ({
    id: task.modelId,
    modelId: task.modelId,
    targetObject: task.targetObject,
    predecessorsIds: task.predecessorsIds,
    atLeastOnePredecessorIds: task.atLeastOnePredecessorIds,
  });
  const tasks = [
    ...targetIndex.tasks.map(({ task }) => toRelinkable(task)),
    ...copiedTasks.map(({ node }) => toRelinkable(node)),
  ];
  const rewrites = planInsertionRewrites(
    tasks,
    insertionPointTaskModelId,
    copiedTasks.map(({ modelId }) => modelId)
  );

  return rewrites.map(({ modelId, previousPredecessorsIds, predecessorsIds, blockedReason }) => ({
    key: `rewire:${modelId}`,
    taskModelId: modelId,
    displayName: getModelName(targetIndex.elementsByModelId.get(modelId).node, input.language),
    previousPredecessorsIds,
    predecessorsIds,
    ...(isNil(blockedReason) ? {} : { blockedReason }),
    isApplied: isNil(blockedReason) && !skipped.includes(modelId),
  }));
}

/** Copied exam tasks whose partner neither the copy nor the target has (see `hasExamPartner`). */
function findUnpairedExamTasks(
  copiedTasks: ProcessModelCopyCopiedElement<ProcessModelCopyTask>[],
  targetIndex: ModelIndex
): ProcessModelCopyExamPairingFinding[] {
  const tasks = [...copiedTasks.map(({ node }) => node), ...targetIndex.tasks.map(({ task }) => task)];
  return copiedTasks.flatMap(({ sourceModelId, modelId, node }) => {
    const typeName = node._typeName;
    if (!isExamTaskTypeName(typeName) || hasExamPartner(node, tasks)) {
      return [];
    }

    return [{ sourceModelId, modelId, typeName }];
  });
}

/**
 * The area and phase the copy goes into that are not visible for every role the copied tasks are for, after the role
 * mapping. One whose roles are unknown is left out: the tree alone has none, and validation reports an empty list. A
 * role still to be mapped is left out as well, its role conflict names it.
 */
function findHiddenContainers(
  copiedTasks: ProcessModelCopyCopiedElement<ProcessModelCopyTask>[],
  input: ProcessModelCopyPlanInput
): ProcessModelCopyContainerVisibilityFinding[] {
  const targetRoleKeys = new Set((input.target.roles ?? []).map(({ processRole }) => processRole?.trim()));
  const taskRoleKeys = uniq(
    copiedTasks
      .flatMap(({ node, sourceModelId }) => collectRoleUsages(node, sourceModelId))
      .filter(({ field }) => TASK_AUDIENCE_ROLE_FIELDS.includes(field))
      .map(({ roleKey }) => roleKey)
  ).filter((roleKey) => targetRoleKeys.has(roleKey));

  return getEnclosingContainers(input).flatMap(({ nodeType, node }) => {
    const visibleForRoles = getVisibleForRoles(node);
    const roleKeys = difference(taskRoleKeys, visibleForRoles);
    if (isEmpty(visibleForRoles) || isEmpty(roleKeys)) {
      return [];
    }

    return [{ nodeType, modelId: node.modelId, roleKeys }];
  });
}

/**
 * The target's areas and phases the copy goes into, innermost first: the phase and its area at task level, the area at
 * phase level. At area level it goes into the model, which no role list hides.
 */
function getEnclosingContainers({
  target,
  level,
  targetContainerModelId,
}: ProcessModelCopyPlanInput): EnclosingContainer[] {
  const areas = target.areas ?? [];
  switch (level) {
    case ProcessModelCopyLevel.Area:
      return [];
    case ProcessModelCopyLevel.Phase:
      return areas
        .filter(({ modelId }) => modelId === targetContainerModelId)
        .map((area): EnclosingContainer => ({ nodeType: ProcessModelCopyNodeType.Area, node: area }));
    case ProcessModelCopyLevel.Task:
      return areas.flatMap((area) =>
        (area.phases ?? [])
          .filter(({ modelId }) => modelId === targetContainerModelId)
          .flatMap((phase): EnclosingContainer[] => [
            { nodeType: ProcessModelCopyNodeType.Phase, node: phase },
            { nodeType: ProcessModelCopyNodeType.Area, node: area },
          ])
      );
    default: {
      const unhandled: never = level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

function assembleStructure(
  selection: ProcessModelCopySelection,
  copiedElements: ProcessModelCopyCopiedElement[],
  idPlan: IdPlan,
  level: ProcessModelCopyLevel
): PlanStructure {
  const copiedBySourceModelId = keyBy(copiedElements, ({ sourceModelId }) => sourceModelId);
  const toTasks = (taskModelIds: string[]): ProcessModelCopyPlannedTask[] =>
    copiedElements
      .filter(isTaskElement)
      .filter(({ sourceModelId }) => taskModelIds.includes(sourceModelId))
      .map(({ sourceModelId, node }) => ({ sourceModelId, task: node }));
  const toExisting = (modelId: string): Pick<ProcessModelCopyPlannedPhase, "modelId" | "existingTargetModelId"> => {
    const isExisting = idPlan.existingModelIds.has(modelId);
    return {
      modelId: isExisting ? modelId : copiedBySourceModelId[modelId].modelId,
      existingTargetModelId: isExisting ? modelId : null,
    };
  };
  const toPhase = ({
    modelId,
    taskModelIds,
  }: {
    modelId: string;
    taskModelIds: string[];
  }): ProcessModelCopyPlannedPhase => ({
    sourceModelId: modelId,
    ...toExisting(modelId),
    phase: copiedBySourceModelId[modelId]?.node ?? null,
    tasks: toTasks(taskModelIds),
  });

  switch (level) {
    case ProcessModelCopyLevel.Task:
      return { tasks: toTasks(selection.taskModelIds), phases: [], areas: [] };
    case ProcessModelCopyLevel.Phase:
      return { tasks: [], phases: selection.phases.map(toPhase), areas: [] };
    case ProcessModelCopyLevel.Area:
      return {
        tasks: [],
        phases: [],
        areas: selection.areas.map(({ modelId, phases }) => ({
          sourceModelId: modelId,
          ...toExisting(modelId),
          area: copiedBySourceModelId[modelId]?.node ?? null,
          phases: phases.map(toPhase),
        })),
      };
    default: {
      const unhandled: never = level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

interface BlockingReasonSources {
  roleConflicts: ProcessModelCopyRoleConflict[];
  referenceConflicts: ProcessModelCopyReferenceConflict[];
  deniedTaskModelIds: string[];
  copiedElements: ProcessModelCopyCopiedElement[];
}

function getBlockingReasons({
  roleConflicts,
  referenceConflicts,
  deniedTaskModelIds,
  copiedElements,
}: BlockingReasonSources): ProcessModelCopyBlockingReason[] {
  const hasUnresolvedConflict = [...roleConflicts, ...referenceConflicts].some(({ isResolved }) => !isResolved);
  return [
    ...(hasUnresolvedConflict ? [ProcessModelCopyBlockingReason.UnresolvedConflict] : []),
    ...(isEmpty(deniedTaskModelIds) ? [] : [ProcessModelCopyBlockingReason.TaskTypeNotAllowed]),
    ...(isEmpty(copiedElements) ? [ProcessModelCopyBlockingReason.NothingToCopy] : []),
  ];
}

function toStringList(value: unknown): string[] {
  return castArray(value).filter(isString);
}

function toUsages(usages: RoleUsage[], roleKey: string): ProcessModelCopyUsage[] {
  return usages
    .filter((usage) => usage.roleKey === roleKey)
    .map(({ sourceModelId, field }) => ({ sourceModelId, field }));
}

function getRoleName(role: ProcessModelCopyRole | nil, language: string): string {
  return isNil(role) ? "" : getModelName({ modelId: role.processRole, displayName: role.displayName }, language);
}

function isTaskElement(
  element: ProcessModelCopyCopiedElement
): element is ProcessModelCopyCopiedElement<ProcessModelCopyTask> {
  return element.nodeType === ProcessModelCopyNodeType.Task;
}
